/**
 * Commands for related operations that should be treated as atomic units
 */

import { CompositeCommand } from './SpecialCommands';
import { BaseCommand } from './BaseCommand';
import { AddMarkerCommand, RemoveMarkerCommand, MoveMarkerCommand } from './MarkerCommands';
import { AddLineCommand, RemoveLineCommand, UpdateLineCommand } from './LineCommands';
import { AddPolygonCommand, RemovePolygonCommand } from './PolygonCommands';
import { MapCommandType } from '../types';
import { IMarkerConfig, ILineConfig, IPolygonConfig, ICoordinate } from '../../types';
import { MarkerManager } from '../../managers/MarkerManager';
import { LineManager } from '../../managers/LineManager';
import { PolygonManager } from '../../managers/PolygonManager';

/**
 * Command for adding a marker with connected line
 */
export class AddMarkerWithLineCommand extends CompositeCommand {
  constructor(
    markerManager: MarkerManager,
    lineManager: LineManager,
    marker: IMarkerConfig,
    line: ILineConfig
  ) {
    const commands = [
      new AddMarkerCommand(markerManager, marker),
      new AddLineCommand(lineManager, line),
    ];

    super(
      commands,
      `Add connected point at ${marker.coordinate.lat}, ${marker.coordinate.lng}`,
      true
    );
  }
}

/**
 * Command for removing a marker with its connected line
 */
export class RemoveMarkerWithLineCommand extends CompositeCommand {
  constructor(
    markerManager: MarkerManager,
    lineManager: LineManager,
    marker: IMarkerConfig,
    line: ILineConfig
  ) {
    const commands = [
      new RemoveLineCommand(lineManager, line),
      new RemoveMarkerCommand(markerManager, marker),
    ];

    super(commands, `Remove connected point ${marker.id}`, true);
  }
}

/**
 * Command for adding a polygon with its corner markers
 */
export class AddPolygonWithMarkersCommand extends CompositeCommand {
  constructor(
    markerManager: MarkerManager,
    polygonManager: PolygonManager,
    polygon: IPolygonConfig,
    markers: IMarkerConfig[]
  ) {
    const commands = [
      new AddPolygonCommand(polygonManager, polygon),
      ...markers.map(marker => new AddMarkerCommand(markerManager, marker)),
    ];

    super(commands, `Add polygon with ${markers.length} corner markers`, true);
  }
}

/**
 * Command for removing a polygon with its corner markers
 */
export class RemovePolygonWithMarkersCommand extends CompositeCommand {
  constructor(
    markerManager: MarkerManager,
    polygonManager: PolygonManager,
    polygon: IPolygonConfig,
    markers: IMarkerConfig[]
  ) {
    const commands = [
      ...markers.map(marker => new RemoveMarkerCommand(markerManager, marker)),
      new RemovePolygonCommand(polygonManager, polygon),
    ];

    super(commands, `Remove polygon with ${markers.length} corner markers`, true);
  }
}

export class AddPolygonCornerCommand extends CompositeCommand {
  constructor(
    markerManager: MarkerManager,
    polygonManager: PolygonManager,
    marker: IMarkerConfig,
    polygon: IPolygonConfig
  ) {
    const commands = [
      new AddMarkerCommand(markerManager, marker),
      new UpdateOrAddPolygonCommand(polygonManager, polygon),
    ];

    super(
      commands,
      `Add polygon corner at ${marker.coordinate.lat}, ${marker.coordinate.lng}`,
      true
    );
  }
}

/**
 * Custom command that updates polygon if exists, or adds if doesn't exist
 */
class UpdateOrAddPolygonCommand extends BaseCommand {
  private polygonManager: PolygonManager;
  private polygon: IPolygonConfig;
  private previousPolygon: IPolygonConfig | null = null;

  constructor(polygonManager: PolygonManager, polygon: IPolygonConfig) {
    super(MapCommandType.UPDATE_POLYGON, `Update or add polygon ${polygon.id}`);
    this.polygonManager = polygonManager;
    this.polygon = polygon;
  }

  execute(): void {
    this.previousPolygon = this.polygonManager.getPolygon(this.polygon.id) || null;

    if (this.previousPolygon) {
      this.polygonManager.updatePolygon(this.polygon);
    } else {
      this.polygonManager.addPolygon(this.polygon);
    }
  }

  undo(): void {
    if (this.previousPolygon) {
      this.polygonManager.updatePolygon(this.previousPolygon);
    } else {
      this.polygonManager.removePolygon(this.polygon.id);
    }
  }

  canExecute(): boolean {
    return true;
  }

  canUndo(): boolean {
    return true;
  }
}

export class AddLineSegmentCommand extends CompositeCommand {
  constructor(
    markerManager: MarkerManager,
    lineManager: LineManager,
    marker: IMarkerConfig,
    newLines: ILineConfig[],
    removedLines?: ILineConfig[]
  ) {
    const commands = [
      new AddMarkerCommand(markerManager, marker),
      // Remove old lines if any (when inserting in middle)
      ...(removedLines || []).map(line => new RemoveLineCommand(lineManager, line)),
      // Add new lines
      ...newLines.map(line => new AddLineCommand(lineManager, line)),
    ];

    super(
      commands,
      `Add line segment point at ${marker.coordinate.lat}, ${marker.coordinate.lng}`,
      true
    );
  }
}

/**
 * Move polygon corner command for drag operations
 */
export class MovePolygonCornerCommand extends CompositeCommand {
  constructor(
    markerManager: MarkerManager,
    polygonManager: PolygonManager,
    markerId: string,
    oldCoordinate: ICoordinate,
    newCoordinate: ICoordinate,
    oldPolygon: IPolygonConfig,
    newPolygon: IPolygonConfig
  ) {
    const commands = [
      new MoveMarkerCommand(markerManager, markerId, oldCoordinate, newCoordinate),
      new UpdateOrAddPolygonCommand(polygonManager, newPolygon),
    ];

    super(
      commands,
      `Move polygon corner ${markerId} from (${oldCoordinate.lat.toFixed(6)}, ${oldCoordinate.lng.toFixed(6)}) to (${newCoordinate.lat.toFixed(6)}, ${newCoordinate.lng.toFixed(6)})`,
      true
    );
  }
}

/**
 * Move line endpoint command for drag operations
 */
export class MoveLineEndpointCommand extends CompositeCommand {
  constructor(
    markerManager: MarkerManager,
    lineManager: LineManager,
    markerId: string,
    oldCoordinate: ICoordinate,
    newCoordinate: ICoordinate,
    affectedLines: Array<{ oldLine: ILineConfig; newLine: ILineConfig }>
  ) {
    const commands = [
      new MoveMarkerCommand(markerManager, markerId, oldCoordinate, newCoordinate),
      ...affectedLines.map(
        ({ oldLine, newLine }) => new UpdateLineCommand(lineManager, newLine, oldLine)
      ),
    ];

    super(
      commands,
      `Move line endpoint ${markerId} from (${oldCoordinate.lat.toFixed(6)}, ${oldCoordinate.lng.toFixed(6)}) to (${newCoordinate.lat.toFixed(6)}, ${newCoordinate.lng.toFixed(6)})`,
      true
    );
  }
}

/**
 * Command for removing a polygon corner point with polygon redraw
 */
export class RemovePolygonCornerCommand extends CompositeCommand {
  constructor(
    markerManager: MarkerManager,
    polygonManager: PolygonManager,
    marker: IMarkerConfig,
    oldPolygon: IPolygonConfig,
    newPolygon?: IPolygonConfig
  ) {
    const commands = [
      new RemoveMarkerCommand(markerManager, marker),
      ...(newPolygon
        ? [new UpdateOrAddPolygonCommand(polygonManager, newPolygon)]
        : [new RemovePolygonCommand(polygonManager, oldPolygon)]),
    ];

    super(
      commands,
      `Remove polygon corner at ${marker.coordinate.lat}, ${marker.coordinate.lng}`,
      true
    );
  }
}

/**
 * Command for adding a line segment with endpoint markers
 */
export class AddLineWithEndpointsCommand extends CompositeCommand {
  constructor(
    markerManager: MarkerManager,
    lineManager: LineManager,
    line: ILineConfig,
    startMarker: IMarkerConfig,
    endMarker: IMarkerConfig
  ) {
    const commands = [
      new AddMarkerCommand(markerManager, startMarker),
      new AddMarkerCommand(markerManager, endMarker),
      new AddLineCommand(lineManager, line),
    ];

    super(commands, `Add line segment with endpoints`, true);
  }
}

/**
 * Command for removing a line segment with its endpoint markers
 */
export class RemoveLineWithEndpointsCommand extends CompositeCommand {
  constructor(
    markerManager: MarkerManager,
    lineManager: LineManager,
    line: ILineConfig,
    startMarker: IMarkerConfig,
    endMarker: IMarkerConfig
  ) {
    const commands = [
      new RemoveLineCommand(lineManager, line),
      new RemoveMarkerCommand(markerManager, startMarker),
      new RemoveMarkerCommand(markerManager, endMarker),
    ];

    super(commands, `Remove line segment with endpoints`, true);
  }
}

/**
 * Command for removing a line point with line reconstruction
 */
export class RemoveLinePointCommand extends CompositeCommand {
  constructor(
    markerManager: MarkerManager,
    lineManager: LineManager,
    marker: IMarkerConfig,
    removedLines: ILineConfig[],
    newLines?: ILineConfig[]
  ) {
    const commands = [
      new RemoveMarkerCommand(markerManager, marker),
      ...removedLines.map(line => new RemoveLineCommand(lineManager, line)),
      ...(newLines || []).map(line => new AddLineCommand(lineManager, line)),
    ];

    super(
      commands,
      `Remove line point at ${marker.coordinate.lat}, ${marker.coordinate.lng}`,
      true
    );
  }
}

/**
 * Command for removing a simple point (no geometry)
 */
export class RemovePointCommand extends CompositeCommand {
  constructor(markerManager: MarkerManager, marker: IMarkerConfig) {
    const commands = [new RemoveMarkerCommand(markerManager, marker)];

    super(commands, `Remove point at ${marker.coordinate.lat}, ${marker.coordinate.lng}`, true);
  }
}
